/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState, useCallback} from 'react'
import {Helmet} from 'react-helmet'
import {collection, onSnapshot, doc, deleteDoc} from '@firebase/firestore'
import db from '../../../services/firebase'
import {useAuth} from '../../modules/auth'
import {PersonnelListToolbar} from './components/header/PersonnelListToolbar'
import personnelServices from './services/personnel.services'
import {PersonnelListLoading} from './components/loading/PersonnelListLoading'
import 'react-modern-drawer/dist/index.css'
import ViewPersonnel from './components/ViewPersonnel'
import Offboarding from './components/Offboarding'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import FormatTimestamp from 'app/components/FormatTimestamp'
import Searchbar from 'app/components/Searchbar'
import {getFunctions, httpsCallable} from 'firebase/functions'
import useAuditContext from 'app/Hooks/useAuditContext'
import {createIntegrationNotifications} from 'app/pages/personnel-management/services/IntegrationNotifications'

type Props = {
  className: string
  allPoliciesChecked: boolean
}
interface UserData {
  uid: string
  id: string
  full_name: string
  role: string
  avatarUrl: string
  tenantId: string
  policiesAccepted?: boolean
  start_date: string
  group: string
  end_date: string
  email: string
  onboardingEnded: string
  onboarding: string
  image_url: string
}

const PersonnelManagement: React.FC<Props> = ({className, allPoliciesChecked}) => {
  const [users, setUsers] = useState<UserData[]>([])
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<UserData[]>([])
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalOpen2, setIsModalOpen2] = useState(false)
  const [personnelID, setPersonnelID] = useState('')
  const [sortBy, setSortBy] = useState('')
  const [sortDirection, setSortDirection] = useState('asc')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
  const [integration, setIntegration] = useState<any[]>([])
  const functions = getFunctions()
  const reinviteEmployeeFunction = httpsCallable(functions, 'reinviteEmployee')
  const sendOnboardingReminderFunction = httpsCallable(functions, 'sendOnboardingReminder')
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    // Delay the rendering of the empty state for 3 seconds
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    setCheckedItems([])
    setSelectAll(false)
  }, [])

  useEffect(() => {
    setFilteredItems(users)
  }, [users])

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    setPersonnelID(id)
  }

  const handleSort = (column) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)
  }

  const renderSortIcon = (column) => {
    if (column === sortBy) {
      return (
        <span
          className={`ms-1 icon-xxs text-muted ${
            sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
          }`}
        />
      )
    }
    return null
  }

  const handleOpenModal2 = (id) => {
    setIsModalOpen2(true)
    setPersonnelID(id)
  }

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(users)
      setSearchLength(false)
    } else {
      const filteredItems = users.filter(
        (user) => user.full_name && user.full_name.toLowerCase().includes(searchTerm)
      )
      setFilteredItems(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  }, [searchTerm, users])

  const getPersonnel = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'employees')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...(doc.data() as UserData),
            id: doc.id,
            image_url: doc.data().image_url || doc.data().photo_url,
          }))
          .filter((item) => item.role !== 'Auditor')

        setUsers(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting personnels:', error)
    }
  }, [currentUser?.tenantId])

  function canOffboard(currentUserRole, targetUserRole, isSelf) {
    // Owner can only offboard themselves
    if (currentUserRole === 'Owner' && isSelf) {
      return true
    }
    // Admin can offboard Members only
    if (currentUserRole === 'Admin' && targetUserRole === 'Member') {
      return true
    }
    // Admin or Owner can offboard Members and Admins (but not Owners, except themselves)
    if (
      (currentUserRole === 'Admin' || currentUserRole === 'Owner') &&
      (targetUserRole === 'Member' || targetUserRole === 'Admin') &&
      !isSelf
    ) {
      return true
    }
    return false
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => (checked ? [...prev, value] : prev.filter((item) => item !== value)))
  }
  const handleSelectAllChange = (e) => {
    setSelectAll(e.target.checked)
    if (e.target.checked) {
      setCheckedItems(
        filteredItems
          .filter((user) => user.tenantId === currentUser?.tenantId)
          .map((item) => item.id)
      )
    } else {
      setCheckedItems([])
    }
  }
  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  const handleCheckedRemoveClick = () => {
    setIsBulkDeleteModalOpen(true)
  }

  const handleConfirmBulkDelete = async () => {
    setIsLoading(true)
    try {
      for (const id of checkedItems) {
        await personnelServices.deletePersonnel(id)
      }
      showSuccessToast({message: 'Employees have been offboarded successfully.'})
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting employees.'})
    } finally {
      setIsLoading(false)
      setIsBulkDeleteModalOpen(false)
      setDropdownVisible(false)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleCloseModal2 = () => {
    setIsModalOpen(false)
  }

  const handleDelete = async (item) => {
    setSelectedEmployee(item)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const employeeRef = collection(tenantRef, 'employees')
      const docRef = doc(employeeRef, selectedEmployee.id)
      await deleteDoc(docRef)

      if (selectedEmployee.uid) {
        const userRef = collection(db, 'users')
        const docsRef = doc(userRef, selectedEmployee.uid)
        await deleteDoc(docsRef)
      }

      showSuccessToast({
        message: `${selectedEmployee.full_name} has been removed from the organization successfully!`,
      })
    } catch (error) {
      showErrorToast({message: 'An error occurred while removing an employee.'})
    } finally {
      setIsLoading(false)
      setIsDeleteModalOpen(false)
    }
  }

  const handleReinvite = async (userId: any) => {
    try {
      const result = await reinviteEmployeeFunction({
        tenantId: currentUser?.tenantId,
        employeeId: userId,
      })
      const data = result.data as {success: boolean}
      if (data.success) {
        showSuccessToast({message: 'Reinvite sent successfully!'})
      }
    } catch (error) {
      showErrorToast({message: 'An error occurred while sending a re-invite email.'})
    }
  }

  const handleOnboardingReminder = async (userId: any) => {
    try {
      const result = await sendOnboardingReminderFunction({
        tenantId: currentUser?.tenantId,
        employeeId: userId,
      })
      const data = result.data as {success: boolean}
      if (data.success) {
        showSuccessToast({message: 'Onboarding reminder sent successfully!'})
      }
    } catch (error: any) {
      showErrorToast({
        message: error.message || 'An error occurred while sending the onboarding reminder.',
      })
    }
  }

  const getIntegration = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'integrations')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const integrationData = snapshot.docs.map((item) => ({
          ...item.data(),
          id: item.id,
          name: item.data().name,
        }))
        setIntegration(integrationData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting integrations:', error)
    }
  }, [currentUser?.tenantId])

  useEffect(() => {
    getPersonnel()
    getIntegration()
  }, [getPersonnel, getIntegration])

  const sendReminder = async (employee: UserData) => {
    try {
      const notificationService = createIntegrationNotifications(integration, currentUser)

      const result = await notificationService.sendOnboardingReminder(employee)

      if (result.success) {
        showSuccessToast({
          message: `Reminder sent successfully via ${result.successfulIntegrations.join(', ')}`,
        })
      } else {
        showErrorToast({
          message: `Failed to send reminder: ${result.errors.join(', ')}`,
        })
      }
    } catch (error: any) {
      console.error('Error sending reminder:', error)
      showErrorToast({message: 'Error sending reminder'})
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Personnel Management</title>
      </Helmet>
      <div className='card-header border-0 pt-6 '>
        <div className='card-title'>
          <div className='flex-lg-fill'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>
        <div className='card-toolbar'>
          {checkedItems.length > 0 ? (
            <>
              {checkedItems.length > 0 ? (
                <div className='  border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                  <span>
                    <p className='pt-4'>{checkedItems.length} Selected</p>
                  </span>
                </div>
              ) : null}

              <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                <img src='/media/buttons/Deselect.svg' alt='Deselect' />
              </div>

              <div className='mb-1'>
                <button
                  type='button'
                  className='btn btn-sm fs-6'
                  style={{height: '40px', background: '#0E9F6E', color: '#fff'}}
                  onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                    (e.currentTarget.style.backgroundColor = '#148363')
                  }
                  onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                    (e.currentTarget.style.backgroundColor = '#0E9F6E')
                  }
                  onClick={handleCheckedRemoveClick}
                  disabled={currentUser?.role === 'Auditor' || !!currentAuditContext}
                >
                  Offboard Employees
                </button>
              </div>
            </>
          ) : (
            <PersonnelListToolbar currentAuditContext={currentAuditContext} />
          )}
        </div>
      </div>

      <div className='card-body py-3' style={{minHeight: '100vh'}}>
        <div className='table-responsive' style={{minHeight: '100vh'}}>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='w-25px'>
                  <div className='form-check form-check-sm form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={selectAll}
                      data-kt-check='true'
                      data-kt-check-target='.widget-9-check'
                      onChange={handleSelectAllChange}
                    />
                  </div>
                </th>
                <th className='min-w-250px' onClick={() => handleSort('fullName')}>
                  NAME {renderSortIcon('fullName')}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('group')}>
                  GROUP {renderSortIcon('group')}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('startDate')}>
                  START DATE {renderSortIcon('startDate')}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('endDate')}>
                  END DATE {renderSortIcon('endDate')}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('onboardingEnded')}>
                  ONBOARDING STATUS {renderSortIcon('onboardingEnded')}
                </th>
                <th className='min-w-100px' onClick={() => handleSort('status')}>
                  STATUS {renderSortIcon('status')}
                </th>
                <th className='min-w-50px'></th>
              </tr>
            </thead>
            <Offboarding isOpen={isModalOpen2} onClose={handleCloseModal2} id={personnelID} />

            {filteredItems && filteredItems.length > 0 ? (
              filteredItems
                .sort((a, b) => {
                  const key = sortBy
                  const direction = sortDirection === 'asc' ? 1 : -1
                  if (a[key] < b[key]) return -1 * direction
                  if (a[key] > b[key]) return 1 * direction
                  return 0
                })
                .map((item) => (
                  <tbody>
                    <tr key={item.id}>
                      <td>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input
                            className='form-check-input widget-9-check'
                            type='checkbox'
                            value={item.id}
                            checked={selectAll || checkedItems.includes(item.id)}
                            onChange={handleCheckChange}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='symbol symbol-35px me-2'>
                            {item.full_name ? (
                              <>
                                {item.image_url ? (
                                  <img
                                    src={item.image_url}
                                    alt={item.full_name}
                                    className='symbol-label fs-6 fw-bold me-2'
                                  />
                                ) : (
                                  <div className='symbol symbol-30px me-2'>
                                    <span
                                      className='symbol-label fs-6 fw-bold'
                                      style={{
                                        backgroundColor: '#F5F5F5',
                                        border: '1px solid #E5E5E5',
                                      }}
                                    >
                                      {item.full_name
                                        ? item.full_name.trim().charAt(0).toUpperCase()
                                        : ''}
                                    </span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <span>-</span>
                              </>
                            )}
                          </div>
                          <div className='d-flex justify-content-start flex-column'>
                            <span
                              className='text-dark fw-bold text-hover-primary fs-6'
                              onClick={() => handleOpenModal(item.id)}
                              style={{cursor: 'pointer'}}
                            >
                              {item.full_name}
                            </span>
                            <span className='text-gray-700 fw-semibold text-muted d-block fs-7'>
                              {item.role}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className='d-flex align-items-center fw-semibold'>
                          <div className='symbol symbol-20px me-15'>
                            <div className=' fs-6'>
                              <div>{item.group}</div>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className='d-flex align-items-center fw-semibold'>
                          <div className='symbol symbol-20px me-15'>
                            <div className=' fs-6'>
                              {FormatTimestamp({timestamp: item.start_date})}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center fw-semibold'>
                          <div className='symbol symbol-20px me-15'>
                            <div className=' fs-6'>
                              {item.end_date ? FormatTimestamp({timestamp: item.end_date}) : '--'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className='d-flex align-items-center fw-bold'>
                          <div className='symbol symbol-20px me-15'>
                            <div className=' fs-6'>
                              <span
                                className='badge fs-7'
                                style={{
                                  color: item.onboarding === 'Completed' ? '#0E9F6E' : '#EE3043',
                                  background:
                                    item.onboarding === 'Completed' ? '#E8F8F4' : '#FFF5F8',
                                }}
                              >
                                {' '}
                                {item.onboarding === 'Completed' ? 'Completed' : 'Incomplete'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        {item?.uid ? (
                          <span
                            className='badge fs-7'
                            style={{
                              color: !item.end_date ? '#0E9F6E' : '#EE3043',
                              background: !item.end_date ? '#E8F8F4' : '#FFF5F8',
                            }}
                          >
                            {!item.end_date ? 'Active' : 'Ended'}
                          </span>
                        ) : (
                          <span
                            className='badge fs-7'
                            style={{
                              color: '#3B82F6',
                              background: '#E3F0FF',
                            }}
                          >
                            Invited
                          </span>
                        )}
                      </td>
                      <td className=''>
                        <div className='dropdown mx-2'>
                          <button
                            className='btn btn-secondary border-0 p-0 m-2'
                            type='button'
                            id='dropdownMenu2'
                            data-bs-toggle='dropdown'
                            aria-haspopup='true'
                            aria-expanded='false'
                          >
                            <img
                              src='/media/buttons/Options.svg'
                              alt='Options'
                              onClick={toggleDropdown}
                            />
                          </button>
                          <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                            <li>
                              <button
                                className='dropdown-item text-hover-primary'
                                type='button'
                                style={{color: '#262626'}}
                                onClick={() => handleOpenModal(item.id)}
                              >
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='18'
                                  height='18'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  stroke-width='2'
                                  stroke-linecap='round'
                                  stroke-linejoin='round'
                                  className='icon icon-tabler icons-tabler-outline icon-tabler-eye mx-1'
                                >
                                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                  <path d='M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0' />
                                  <path d='M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6' />
                                </svg>{' '}
                                View Employee
                              </button>
                            </li>
                            {createIntegrationNotifications(
                              integration,
                              currentUser
                            ).isReminderAvailable() &&
                              item.onboarding !== 'Completed' && (
                                <li>
                                  <button
                                    className='dropdown-item text-hover-primary'
                                    type='button'
                                    style={{color: '#262626'}}
                                    onClick={() => sendReminder(item)}
                                  >
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='18'
                                      height='18'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='currentColor'
                                      stroke-width='2'
                                      stroke-linecap='round'
                                      stroke-linejoin='round'
                                      className='icon icon-tabler icons-tabler-outline icon-tabler-brand-slack mx-1 mb-1'
                                    >
                                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                      <path d='M12 12v-6a2 2 0 0 1 4 0v6m0 -2a2 2 0 1 1 2 2h-6' />
                                      <path d='M12 12h6a2 2 0 0 1 0 4h-6m2 0a2 2 0 1 1 -2 2v-6' />
                                      <path d='M12 12v6a2 2 0 0 1 -4 0v-6m0 2a2 2 0 1 1 -2 -2h6' />
                                      <path d='M12 12h-6a2 2 0 0 1 0 -4h6m-2 0a2 2 0 1 1 2 -2v6' />
                                    </svg>{' '}
                                    Send Reminder
                                  </button>
                                </li>
                              )}
                            {item.onboarding !== 'Completed' && (
                              <li>
                                <button
                                  className='dropdown-item text-hover-primary'
                                  type='button'
                                  style={{color: '#262626'}}
                                  onClick={() => handleOnboardingReminder(item.id)}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-mail mx-1 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                                    <path d='M3 7l9 6l9 -6' />
                                  </svg>
                                  Onboarding Reminder
                                </button>
                              </li>
                            )}
                            {!item.uid && (
                              <li>
                                <button
                                  className='dropdown-item text-hover-primary'
                                  type='button'
                                  style={{color: '#262626'}}
                                  onClick={() => handleReinvite(item.id)}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-send mx-1 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M10 14l11 -11' />
                                    <path d='M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5' />
                                  </svg>{' '}
                                  Resend Invite
                                </button>
                              </li>
                            )}
                            <li>
                              <button
                                className={`dropdown-item text-hover-danger ${
                                  !canOffboard(
                                    currentUser?.role,
                                    item.role,
                                    currentUser?.uid === item.id
                                  ) || !!currentAuditContext
                                    ? 'text-muted'
                                    : ''
                                }`}
                                type='button'
                                style={{color: '#262626'}}
                                onClick={() => handleOpenModal2(item.id)}
                                disabled={
                                  !canOffboard(
                                    currentUser?.role,
                                    item.role,
                                    currentUser?.uid === item.id
                                  ) || !!currentAuditContext
                                }
                              >
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='18'
                                  height='18'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  stroke-width='2'
                                  stroke-linecap='round'
                                  stroke-linejoin='round'
                                  className='icon icon-tabler icons-tabler-outline icon-tabler-user-minus mx-1 mb-1'
                                >
                                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                  <path d='M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0' />
                                  <path d='M6 21v-2a4 4 0 0 1 4 -4h4c.348 0 .686 .045 1.009 .128' />
                                  <path d='M16 19h6' />
                                </svg>{' '}
                                Offboard Employee
                              </button>
                            </li>
                            {currentUser?.role === 'Owner' && currentUser.uid !== item.id ? (
                              <>
                                <div className='separator my-1'></div>
                                <li>
                                  <button
                                    className='dropdown-item text-danger'
                                    type='button'
                                    style={{color: '#F15363'}}
                                    onClick={(e) => handleDelete(item)}
                                  >
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='18'
                                      height='18'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='currentColor'
                                      stroke-width='2'
                                      stroke-linecap='round'
                                      stroke-linejoin='round'
                                      className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                                    >
                                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                      <path d='M4 7l16 0' />
                                      <path d='M10 11l0 6' />
                                      <path d='M14 11l0 6' />
                                      <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                      <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                    </svg>{' '}
                                    Remove Employee
                                  </button>
                                </li>
                              </>
                            ) : null}
                          </ul>
                        </div>
                      </td>
                    </tr>{' '}
                  </tbody>
                ))
            ) : (
              <tbody>
                <tr>
                  {searchLength ? (
                    <td colSpan={6} className='text-center'>
                      <div className='m-20'>
                        <img src='/media/buttons/emptystate.svg' alt='' />
                        <div className='m-5 fs-5'>
                          <p style={{fontWeight: 'bold'}}>No Employees Found</p>
                        </div>
                        <p className=''>
                          We couldn't find any employee that match your search. Maybe try a
                          different search!
                        </p>
                      </div>
                    </td>
                  ) : (
                    <td colSpan={6}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        {!showEmptyState ? (
                          <div className='m-20'>
                            <PersonnelListLoading />
                          </div>
                        ) : (
                          <div className='m-20'>
                            <img src='/media/buttons/emptystate.svg' alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No Employees Added</p>
                            </div>
                            <p>
                              All the employees within your organization are required to address
                              here.
                            </p>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>
      <ViewPersonnel isOpen={isModalOpen} onClose={handleCloseModal} id={personnelID} />
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => setIsDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedEmployee?.full_name}
        isLoading={isLoading}
        title={`Remove ${selectedEmployee?.full_name}`}
        message={`Are you sure you want to remove ${selectedEmployee?.full_name} from the organization? This action cannot be undone.`}
        confirmButtonText='Remove'
        type='delete'
      />

      <ConfirmationModal
        isModalOpen={isBulkDeleteModalOpen}
        hideModal={() => setIsBulkDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmBulkDelete}
        toDeleteItemTitle='selected employees'
        isLoading={isLoading}
        title='Remove checked employees'
        message='Are you sure you want to remove checked employees from the organization? This action cannot be undone.'
        confirmButtonText='Remove'
        type='delete'
      />
    </>
  )
}

export {PersonnelManagement}
